import { ReactElement, useEffect, useRef } from 'react'
import type { SearchCategory } from 'const/searchItems'
import type { ResolvedSearchItem } from 'hooks/useSemanticSearch'
import type { EligibleAccount } from 'hooks/useAccountContexts'
import i18n from 'strings/i18n'
import type { SearchPhase } from './index'
import {
  AccountDetails,
  AccountItem,
  AccountLast4,
  AccountName,
  AccountPickerContainer,
  AccountPickerHeader,
  CategoryHeader,
  DropdownPanel,
  EmptyState,
  ResultItem,
  ResultLabel,
  ResultsList
} from './subcomponents'

const categoryKeys = {
  'Account Actions': 'categoryAccountActions',
  'Card Actions': 'categoryCardActions',
  Security: 'categorySecurity',
  Pages: 'categoryPages'
} as const satisfies Record<SearchCategory | string, string>

interface SearchResultsProps {
  results: ResolvedSearchItem[]
  activeIndex: number
  onSelect: (item: ResolvedSearchItem) => void
}

const SearchResults = ({
  results,
  activeIndex,
  onSelect
}: SearchResultsProps): ReactElement => {
  if (results.length === 0) {
    return (
      <EmptyState data-test="search-empty-state">
        {i18n({ search: 'noResults' })}
      </EmptyState>
    )
  }

  let lastCategory: SearchCategory | null = null

  return (
    <ResultsList role="listbox" id="search-results-listbox">
      {results.map((item, index) => {
        const showHeader = item.category !== lastCategory
        lastCategory = item.category
        return (
          <div key={item.id}>
            {showHeader && (
              <CategoryHeader>
                {i18n({ search: categoryKeys[item.category] })}
              </CategoryHeader>
            )}
            <ResultItem
              $isActive={index === activeIndex}
              onClick={() => onSelect(item)}
              role="option"
              tabIndex={-1}
              aria-selected={index === activeIndex}
              data-test={`search-result-${item.id}`}
            >
              <ResultLabel>{item.label}</ResultLabel>
            </ResultItem>
          </div>
        )
      })}
    </ResultsList>
  )
}

/** Non-interactive echo of the chosen feature while the account picker is open. */
const SelectedItem = ({ item }: { item: ResolvedSearchItem }): ReactElement => (
  <ResultsList>
    <CategoryHeader>
      {i18n({ search: categoryKeys[item.category] })}
    </CategoryHeader>
    <ResultItem as="div">
      <ResultLabel>{item.label}</ResultLabel>
    </ResultItem>
  </ResultsList>
)

interface AccountPickerProps {
  accounts: EligibleAccount[]
  activeIndex: number
  onSelect: (account: EligibleAccount) => void
  emptyMessage?: string
}

const AccountPicker = ({
  accounts,
  activeIndex,
  onSelect,
  emptyMessage
}: AccountPickerProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null)

  // Auto-focus the first option when the picker becomes actionable.
  useEffect(() => {
    if (accounts.length > 1) {
      containerRef.current
        ?.querySelector<HTMLElement>('[role="option"]')
        ?.focus()
    }
  }, [accounts.length])

  if (accounts.length === 0 && emptyMessage) {
    return (
      <AccountPickerContainer data-test="search-account-picker">
        <EmptyState>{emptyMessage}</EmptyState>
      </AccountPickerContainer>
    )
  }

  return (
    <AccountPickerContainer
      ref={containerRef}
      data-test="search-account-picker"
    >
      <AccountPickerHeader>
        {i18n({ search: 'selectAccount' })}
      </AccountPickerHeader>
      {accounts.map((account, index) => (
        <AccountItem
          key={account.accountId}
          $isActive={index === activeIndex}
          onClick={() => onSelect(account)}
          role="option"
          aria-selected={index === activeIndex}
          data-test={`search-account-${account.accountId}`}
        >
          <AccountDetails>
            <AccountName>{account.displayName}</AccountName>
            <AccountLast4>...{account.accountLast4}</AccountLast4>
          </AccountDetails>
        </AccountItem>
      ))}
    </AccountPickerContainer>
  )
}

export interface SearchDropdownState {
  query: string
  phase: SearchPhase
  results: ResolvedSearchItem[]
  selectedItem: ResolvedSearchItem | null
  activeIndex: number
}

export interface SearchDropdownProps {
  state: SearchDropdownState
  accounts: EligibleAccount[]
  onSelectItem: (item: ResolvedSearchItem) => void
  onSelectAccount: (account: EligibleAccount) => void
}

const SearchDropdown = ({
  state,
  accounts,
  onSelectItem,
  onSelectAccount
}: SearchDropdownProps): ReactElement => {
  const { phase, results, selectedItem, activeIndex } = state
  return (
    <DropdownPanel>
      {phase === 'results' && (
        <SearchResults
          results={results}
          activeIndex={activeIndex}
          onSelect={onSelectItem}
        />
      )}
      {phase === 'accounts' && selectedItem && (
        <>
          <SelectedItem item={selectedItem} />
          <AccountPicker
            accounts={accounts}
            activeIndex={activeIndex}
            onSelect={onSelectAccount}
            emptyMessage={`${i18n({ search: 'featureUnavailable' })}`}
          />
        </>
      )}
    </DropdownPanel>
  )
}

export default SearchDropdown
